import { decamelizeKeys } from 'humps'
import { apiClient } from '../constants'

export const getProjects = (params: Record<string, string | number>) =>
  apiClient.get<Project[]>(`/projects`, { params })

export const createProject = (name: Project['name']) =>
  apiClient.post<Project>(`/projects`, { name })

export const getProject = (id?: Project['id']) =>
  apiClient.get<Project>(`/projects/${id}`)

export const destroyProject = (id: Project['id']) =>
  apiClient.delete(`/projects/${id}`)

export const updateProject = (id: Project['id'], params: UpdateProjectPayload | FormData) =>
  apiClient.put<Project>(`/projects/${id}`, params)

export const shareProject = (id: Project['id'], params: ShareProjectPayload) =>
  apiClient.post(`/projects/${id}/share`, params)

export type GcodeGenerationResult
  = | { status: 'pending' }
    | { status: 'ready', gcode: string }
    | { status: 'failed' }

// Asynchronous gcode retrieval: the backend answers 202 while the slicing job is
// still running, 200 with the gcode body once it is ready, and 422 when the
// generation has failed for good. All three are expected poll outcomes, so we
// whitelist them via `validateStatus` and return them as data — only genuine
// transport failures (5xx, network) reject, which lets the polling query tell a
// transient hiccup apart from a real failure and retry accordingly.
// Passing `retry: true` (used to manually retry a failed generation) tells the
// backend to re-enqueue the slicing job instead of returning the cached failure.
export const fetchProjectGcode = async (
  id: Project['id'],
  printerId: string,
  { retry }: { retry?: boolean } = {},
): Promise<GcodeGenerationResult> => {
  const response = await apiClient.get<Blob>(`/projects/${id}/gcode`, {
    params: decamelizeKeys({ printerId, retry }),
    responseType: 'blob',
    validateStatus: status => status === 200 || status === 202 || status === 422,
  })

  if (response.status === 202) {
    return { status: 'pending' }
  }

  if (response.status === 422) {
    return { status: 'failed' }
  }

  return { status: 'ready', gcode: await response.data.text() }
}
